<template>
  <div class="det-root">

    <!-- Loading state -->
    <template v-if="store.loading || customerLoading">
      <div class="state-wrap">
        <div class="nd-icon">⏳</div>
        <h2>กำลังโหลดข้อมูล…</h2>
      </div>
    </template>

    <!-- Not found -->
    <template v-else-if="!customer">
      <div class="state-wrap">
        <div class="nd-icon">🔍</div>
        <h2>ไม่พบข้อมูลลูกค้า</h2>
        <p>ไม่พบ "{{ decodedId }}" ในผลการวิเคราะห์</p>
        <button class="btn-back" @click="router.push({ name: 'CsDashboard' })">← กลับ Dashboard</button>
      </div>
    </template>

    <template v-else>
      <!-- Page header -->
      <div class="page-hdr">
        <button class="back-btn" @click="router.push({ name: 'CsPortfolio' })">← Portfolio</button>
        <div class="hdr-customer">
          <div class="hdr-name">
            {{ customer.customerName }}
            <span :class="'tier-hdr tier-hdr-' + customer.tierKey">{{ customer.tier }}</span>
          </div>
          <div class="hdr-sub">ข้อมูล ณ วันที่ {{ analysisDate }}</div>
        </div>
        <div class="hdr-scores">
          <div class="hdr-score">
            <div class="hdr-score-lbl">Risk</div>
            <div class="hdr-score-val hdr-val-risk">{{ fmtNum(customer.riskFinal, 2) }}</div>
          </div>
          <div class="hdr-div"></div>
          <div class="hdr-score">
            <div class="hdr-score-lbl">Value</div>
            <div class="hdr-score-val hdr-val-value">{{ fmtNum(customer.valueFinal, 2) }}</div>
          </div>
          <div class="hdr-div"></div>
          <div class="hdr-score">
            <div class="hdr-score-lbl">DMI</div>
            <div class="hdr-score-val hdr-val-dmi">{{ fmtNum(customer.dmi, 2) }}</div>
          </div>
        </div>
      </div>

      <div class="det-main">

        <!-- Warning banner -->
        <div v-if="warnings.length > 0" class="warn-banner">
          <span style="font-size:1rem;flex-shrink:0">⚠️</span>
          <span class="warn-text">
            <strong>ต้องติดตาม:</strong>
            <span v-for="(w, i) in warnings" :key="i">
              <span v-if="i > 0"> · </span>
              <span v-html="w"></span>
            </span>
          </span>
        </div>

        <!-- Score hero grid -->
        <div class="section score-hero-grid">

          <!-- Risk card -->
          <div class="score-card risk">
            <div class="score-card-lbl">คะแนนความเสี่ยง (Risk Score)</div>
            <div class="score-main-val">{{ fmtNum(customer.riskFinal, 2) }}</div>
            <span class="score-band-badge" :class="bandCls(customer.riskFinal)">● {{ bandLabelFull(customer.riskFinal) }}</span>
            <div class="score-gauge-bg"><div class="score-gauge-fill risk-fill" :style="{ width: customer.riskFinal + '%' }"></div></div>
            <div class="score-range-lbl"><span>0</span><span>34</span><span>67</span><span>100</span></div>
            <div class="score-meta">
              <div>
                <div class="score-meta-lbl">Raw</div>
                <div class="score-meta-val">{{ fmtNum(customer.riskRaw, 2) }}</div>
              </div>
              <div>
                <div class="score-meta-lbl">Anchored</div>
                <div class="score-meta-val">{{ fmtNum(customer.riskAnchored, 2) }}</div>
              </div>
              <div>
                <div class="score-meta-lbl">ปรับสุทธิ</div>
                <div class="score-meta-val" :style="adjStyle(riskTotalAdj)">{{ fmtAdj(riskTotalAdj) }}</div>
              </div>
            </div>
          </div>

          <!-- Value card -->
          <div class="score-card value">
            <div class="score-card-lbl">คะแนนมูลค่า (Value Score)</div>
            <div class="score-main-val">{{ fmtNum(customer.valueFinal, 2) }}</div>
            <span class="score-band-badge" :class="bandCls(customer.valueFinal)">● {{ bandLabelFull(customer.valueFinal) }}</span>
            <div class="score-gauge-bg"><div class="score-gauge-fill value-fill" :style="{ width: customer.valueFinal + '%' }"></div></div>
            <div class="score-range-lbl"><span>0</span><span>34</span><span>67</span><span>100</span></div>
            <div class="score-meta">
              <div>
                <div class="score-meta-lbl">Raw</div>
                <div class="score-meta-val">{{ fmtNum(customer.valueRaw, 2) }}</div>
              </div>
              <div>
                <div class="score-meta-lbl">Anchored</div>
                <div class="score-meta-val">{{ fmtNum(customer.valueAnchored, 2) }}</div>
              </div>
              <div>
                <div class="score-meta-lbl">ปรับสุทธิ</div>
                <div class="score-meta-val" :style="adjStyle(valueTotalAdj)">{{ fmtAdj(valueTotalAdj) }}</div>
              </div>
            </div>
          </div>

          <!-- DMI card -->
          <div class="score-card dmi">
            <div class="score-card-lbl">ดัชนีความสมบูรณ์ข้อมูล (DMI)</div>
            <div class="score-main-val">{{ fmtNum(customer.dmi, 2) }}</div>
            <span class="score-band-badge" :class="dmiCls(customer.dmi)">● {{ dmiLabel(customer.dmi) }}</span>
            <div class="score-gauge-bg"><div class="score-gauge-fill dmi-fill" :style="{ width: Math.min(customer.dmi, 100) + '%' }"></div></div>
            <div class="score-range-lbl"><span>0</span><span>30</span><span>70</span><span>100</span></div>
            <div class="score-meta">
              <div>
                <div class="score-meta-lbl">ผลต่อ Risk</div>
                <div class="score-meta-val" :style="adjStyle(riskAnchorDelta)">{{ fmtAdj(riskAnchorDelta) }}</div>
              </div>
              <div>
                <div class="score-meta-lbl">ผลต่อ Value</div>
                <div class="score-meta-val" :style="adjStyle(valueAnchorDelta)">{{ fmtAdj(valueAnchorDelta) }}</div>
              </div>
              <div>
                <div class="score-meta-lbl">DMI Ceiling</div>
                <div class="score-meta-val" :style="customer.dmi < 70 ? 'color:#d97706' : 'color:#9ca3af'">
                  {{ customer.dmi < 70 ? 'ใช้งาน' : 'ไม่ใช้' }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Breakdown + Ticket status 2-col -->
        <div class="section cs-col-2">

          <!-- Left: Score breakdown -->
          <div class="cs-card">
            <div class="cs-card-title-row">
              <span class="cs-card-title">รายละเอียดองค์ประกอบคะแนน</span>
              <button class="guide-btn" title="วิธีอ่านตาราง" @click="guideModalOpen = true">
                <span class="guide-btn-lbl">วิธีการอ่านคะแนน</span>?
              </button>
            </div>
            <div class="breakdown-cols">
              <div>
                <div class="breakdown-hdr risk-hdr">Risk Score</div>
                <table class="breakdown-table">
                  <tr>
                    <td>Risk Raw</td>
                    <td>{{ fmtNum(customer.riskRaw, 2) }}</td>
                  </tr>
                  <tr>
                    <td class="adj-lbl">DMI Anchoring</td>
                    <td :class="adjClass(riskAnchorDelta)">{{ fmtAdj(riskAnchorDelta) }}</td>
                  </tr>
                  <tr class="bk-divider"><td colspan="2"></td></tr>
                  <tr>
                    <td>Risk Anchored</td>
                    <td style="font-weight:700">{{ fmtNum(customer.riskAnchored, 2) }}</td>
                  </tr>
                  <tr class="bk-divider"><td colspan="2"></td></tr>
                  <tr>
                    <td class="adj-lbl">Risk Trend Adj.</td>
                    <td :class="adjClass(customer.riskTrendAdj)">{{ fmtAdj(customer.riskTrendAdj) }}</td>
                  </tr>
                  <tr>
                    <td class="adj-lbl">Late Renewal Adj.</td>
                    <td :class="adjClass(customer.lateRenewalTrendAdj)">{{ fmtAdj(customer.lateRenewalTrendAdj) }}</td>
                  </tr>
                  <tr>
                    <td class="adj-lbl">Early Warning Adj.</td>
                    <td :class="adjClass(customer.earlyWarningAdj)">{{ fmtAdj(customer.earlyWarningAdj) }}</td>
                  </tr>
                  <tr class="bk-final">
                    <td>Risk Final</td>
                    <td style="color:#d97706;font-size:1.05rem">{{ fmtNum(customer.riskFinal, 2) }}</td>
                  </tr>
                </table>
              </div>
              <div>
                <div class="breakdown-hdr value-hdr">Value Score</div>
                <table class="breakdown-table">
                  <tr>
                    <td>Value Raw</td>
                    <td>{{ fmtNum(customer.valueRaw, 2) }}</td>
                  </tr>
                  <tr>
                    <td class="adj-lbl">DMI Anchoring</td>
                    <td :class="adjClass(valueAnchorDelta)">{{ fmtAdj(valueAnchorDelta) }}</td>
                  </tr>
                  <tr class="bk-divider"><td colspan="2"></td></tr>
                  <tr>
                    <td>Value Anchored</td>
                    <td style="font-weight:700">{{ fmtNum(customer.valueAnchored, 2) }}</td>
                  </tr>
                  <tr class="bk-divider"><td colspan="2"></td></tr>
                  <tr>
                    <td class="adj-lbl">Value Trend Adj.</td>
                    <td :class="adjClass(customer.valueTrendAdj)">{{ fmtAdj(customer.valueTrendAdj) }}</td>
                  </tr>
                  <tr class="bk-final">
                    <td>Value Final</td>
                    <td style="color:#059669;font-size:1.05rem">{{ fmtNum(customer.valueFinal, 2) }}</td>
                  </tr>
                </table>
                <!-- Tier result box -->
                <div class="tier-result-box">
                  <div class="tier-result-lbl">ผลการจัดกลุ่ม</div>
                  <div class="tier-result-body">
                    Risk: <strong>{{ bandLabelFull(customer.riskFinal) }}</strong> ({{ fmtNum(customer.riskFinal, 2) }})
                    × Value: <strong>{{ bandLabelFull(customer.valueFinal) }}</strong> ({{ fmtNum(customer.valueFinal, 2) }})<br>
                    → <span :class="'tier-badge tier-' + customer.tierKey" style="font-size:.85rem;">{{ customer.tier }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right: Ticket status -->
          <div class="cs-card">
            <div class="cs-card-title">สถานะตั๋วจำนำ</div>
            <div class="ticket-stat-row">
              <div class="ticket-stat">
                <div class="ticket-stat-lbl">ตั๋วเปิดอยู่</div>
                <div class="ticket-stat-val tv-open">{{ customer.openChains }}</div>
                <div class="ticket-stat-sub">เงินต้น {{ fmtBaht(customer.openPrincipal) }}</div>
              </div>
              <div class="ticket-stat">
                <div class="ticket-stat-lbl">ตั๋วค้างชำระ</div>
                <div class="ticket-stat-val" :class="customer.overdueChains > 0 ? 'tv-overdue' : 'tv-ok'">{{ customer.overdueChains }}</div>
                <div class="ticket-stat-sub" :style="customer.overdueChains > 0 ? 'color:var(--red)' : ''">
                  {{ customer.overdueChains > 0
                    ? (fmtBaht(customer.overduePrincipal) + ' · ' + customer.maxDaysOverdue + ' วัน')
                    : 'ไม่มีค้างชำระ' }}
                </div>
              </div>
              <div class="ticket-stat">
                <div class="ticket-stat-lbl">ตั๋วปิดแล้ว</div>
                <div class="ticket-stat-val tv-closed">{{ customer.redeemedChains }}</div>
                <div class="ticket-stat-sub">ไถ่ถอนแล้ว</div>
              </div>
              <div class="ticket-stat">
                <div class="ticket-stat-lbl">เคยหลุด</div>
                <div class="ticket-stat-val" :class="customer.forfeitedChains > 0 ? 'tv-amber' : 'tv-ok'">{{ customer.forfeitedChains }}</div>
                <div class="ticket-stat-sub">จาก {{ totalTickets }} ตั๋วทั้งหมด</div>
              </div>
            </div>
            <div class="divider"></div>
            <div v-if="customer.overdueChains > 0" class="overdue-box">
              <div class="overdue-box-lbl">รายละเอียดค้างชำระ</div>
              <div class="overdue-row">
                <span class="overdue-key">จำนวนตั๋วค้างชำระ</span>
                <span class="overdue-val">{{ customer.overdueChains }} ใบ</span>
              </div>
              <div class="overdue-row">
                <span class="overdue-key">เงินต้นค้างชำระ</span>
                <span class="overdue-val">{{ fmtBaht(customer.overduePrincipal) }}</span>
              </div>
              <div class="overdue-row">
                <span class="overdue-key">ค้างชำระสูงสุด</span>
                <span class="overdue-val">{{ customer.maxDaysOverdue }} วัน</span>
              </div>
            </div>
            <div v-else class="no-overdue">✓ ไม่มีตั๋วค้างชำระ</div>
            <div class="principal-summary">
              <div class="ps-row">
                <span>เงินต้นรวม (เปิดทั้งหมด)</span>
                <strong>{{ fmtBaht(customer.openPrincipal) }}</strong>
              </div>
              <div v-if="customer.overdueChains > 0" class="ps-row" style="color:var(--red)">
                <span>เงินต้นค้างชำระ</span>
                <strong>{{ fmtBaht(customer.overduePrincipal) }}</strong>
              </div>
            </div>
          </div>
        </div>

        <!-- Trend strip -->
        <div class="section">
          <div class="section-lbl">การปรับคะแนนตามแนวโน้ม (Trend Adjustments)</div>
          <div class="trend-strip">
            <div v-for="t in trends" :key="t.lbl" class="trend-card">
              <div class="trend-card-lbl">{{ t.lbl }}</div>
              <div class="trend-card-val" :class="sigCls(t.val)">{{ fmtAdj(t.val) }}</div>
              <div class="trend-card-desc" :class="sigCls(t.val)">{{ t.desc }}</div>
            </div>
          </div>
        </div>

        <!-- History strip -->
        <div class="section">
          <div class="section-lbl">ประวัติสรุป</div>
          <div class="history-strip">
            <div class="history-item">
              <div class="history-lbl">รายการทั้งหมด</div>
              <div class="history-val">{{ customer.totalTransactions }}</div>
              <div class="history-sub">transactions</div>
            </div>
            <div class="history-item">
              <div class="history-lbl">ตั๋วเปิดอยู่</div>
              <div class="history-val">{{ customer.openChains }}</div>
              <div class="history-sub">{{ fmtBaht(customer.openPrincipal) }} เงินต้น</div>
            </div>
            <div class="history-item">
              <div class="history-lbl">ตั๋วค้างชำระ</div>
              <div class="history-val" :class="customer.overdueChains > 0 ? 'h-red' : ''">{{ customer.overdueChains }}</div>
              <div class="history-sub">{{ customer.overdueChains > 0 ? ('สูงสุด ' + customer.maxDaysOverdue + ' วัน') : 'ไม่มีค้างชำระ' }}</div>
            </div>
            <div class="history-item">
              <div class="history-lbl">ตั๋วปิดแล้ว</div>
              <div class="history-val">{{ customer.redeemedChains }}</div>
              <div class="history-sub">จาก {{ totalTickets }} ทั้งหมด</div>
            </div>
            <div class="history-item">
              <div class="history-lbl">เคยหลุด</div>
              <div class="history-val" :class="customer.forfeitedChains > 0 ? 'h-amber' : ''">{{ customer.forfeitedChains }}</div>
              <div class="history-sub">{{ totalTickets > 0 ? (Math.round(customer.forfeitedChains / totalTickets * 100) + '% ของทั้งหมด') : '—' }}</div>
            </div>
          </div>
        </div>

      </div>
    </template>

    <!-- Score guide modal -->
    <Transition name="modal">
      <div v-if="guideModalOpen" class="modal-overlay" @click.self="guideModalOpen = false">
        <div class="modal-box">
          <div class="modal-hdr">
            <span>วิธีอ่านตารางรายละเอียดองค์ประกอบคะแนน</span>
            <button class="modal-close" @click="guideModalOpen = false">✕</button>
          </div>
          <div class="modal-body">
            <table class="guide-table">
              <thead>
                <tr>
                  <th>องค์ประกอบ</th>
                  <th>ความหมาย</th>
                  <th>วิธีอ่านค่า</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="guide-name">Raw Score</td>
                  <td>คะแนนดิบก่อนปรับ คำนวณจากปัจจัยพื้นฐาน เช่น อัตราหลุด, ความถี่, มูลค่าดอกเบี้ย</td>
                  <td><span class="guide-hint">ยิ่งสูงยิ่งดี (0–100)</span></td>
                </tr>
                <tr class="guide-sep"><td colspan="3"></td></tr>
                <tr>
                  <td class="guide-name">DMI Anchoring</td>
                  <td>ปรับ Raw Score เข้าหาค่ากลาง (50) ตาม DMI — ถ้า DMI ต่ำ (ข้อมูลน้อย) คะแนนจะถูกดึงมาหา 50 มากขึ้น เพื่อไม่ให้ตัดสินจากข้อมูลไม่พอ</td>
                  <td><span class="guide-pos">+ ดีกว่า 50</span> / <span class="guide-neg">− ต่ำกว่า 50</span></td>
                </tr>
                <tr>
                  <td class="guide-name">Anchored Score</td>
                  <td>คะแนนหลังผ่าน DMI Anchoring แล้ว — เป็นฐานก่อนปรับด้วย Trend</td>
                  <td><span class="guide-hint">ตัวหนา — ค่ากลางของกระบวนการ</span></td>
                </tr>
                <tr class="guide-sep"><td colspan="3"></td></tr>
                <tr>
                  <td class="guide-name">Risk Trend Adj.</td>
                  <td>ปรับตามแนวโน้มอัตราหลุดใน 6 เดือนล่าสุด เทียบกับ 6 เดือนก่อนหน้า</td>
                  <td><span class="guide-pos">+ หลุดน้อยลง</span> / <span class="guide-neg">− หลุดมากขึ้น</span></td>
                </tr>
                <tr>
                  <td class="guide-name">Late Renewal Adj.</td>
                  <td>ปรับตามพฤติกรรมต่อดอกช้า (เลยกำหนดแต่ยังไม่หมดผลัด)</td>
                  <td><span class="guide-pos">+ ต่อตรงเวลาขึ้น</span> / <span class="guide-neg">− ต่อช้าลง</span></td>
                </tr>
                <tr>
                  <td class="guide-name">Early Warning Adj.</td>
                  <td>หักคะแนนเมื่อตั๋วที่เปิดอยู่เริ่มค้างชำระ — ยิ่งค้างมากสัดส่วนเงินต้น ยิ่งหักมาก</td>
                  <td><span class="guide-neu">0 ปกติ</span> / <span class="guide-neg">− ค้างชำระ 15–60%+</span></td>
                </tr>
                <tr>
                  <td class="guide-name">Value Trend Adj.</td>
                  <td>ปรับตามแนวโน้มมูลค่าดอกเบี้ยที่ร้านได้รับ เทียบช่วง 6 เดือน</td>
                  <td><span class="guide-pos">+ รายได้เพิ่ม</span> / <span class="guide-neg">− รายได้ลด</span></td>
                </tr>
                <tr class="guide-sep"><td colspan="3"></td></tr>
                <tr class="guide-final-row">
                  <td class="guide-name">Final Score</td>
                  <td>คะแนนสุทธิที่ใช้จัดกลุ่ม Tier — ≥67 = สูง (HIGH), 34–66 = กลาง (MID), &lt;34 = ต่ำ (LOW)</td>
                  <td>
                    <span class="band-pill band-high">HIGH ≥67</span>
                    <span class="band-pill band-mid">MID 34–66</span>
                    <span class="band-pill band-low">LOW &lt;34</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watchEffect } from 'vue'
import { useRouter } from 'vue-router'
import { useScoreStore } from '@/stores/score.js'

const router = useRouter()
const store = useScoreStore()
const guideModalOpen = ref(false)

function onKeydown(e) { if (e.key === 'Escape') guideModalOpen.value = false }
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

onMounted(() => { store.init(); window.addEventListener('keydown', onKeydown) })

const customer        = ref(null)
const customerLoading = ref(false)

watchEffect(async () => {
  const id = store.selectedCustomerId
  if (!id) { customer.value = null; return }
  customerLoading.value = true
  try {
    customer.value = await store.fetchCustomer(id)
  } finally {
    customerLoading.value = false
  }
})

const analysisDate = computed(() => {
  const ts = store.timestamp
  if (!ts) return 'ไม่ทราบวันที่'
  return new Date(ts).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })
})

const riskAnchorDelta  = computed(() => customer.value ? customer.value.riskAnchored  - customer.value.riskRaw  : 0)
const valueAnchorDelta = computed(() => customer.value ? customer.value.valueAnchored - customer.value.valueRaw : 0)
const riskTotalAdj     = computed(() => customer.value
  ? customer.value.riskTrendAdj + customer.value.lateRenewalTrendAdj + customer.value.earlyWarningAdj
  : 0)
const valueTotalAdj    = computed(() => customer.value ? customer.value.valueTrendAdj : 0)

const totalTickets = computed(() => customer.value
  ? customer.value.openChains + customer.value.redeemedChains + (customer.value.forfeitedChains || 0)
  : 0)

const warnings = computed(() => {
  if (!customer.value) return []
  const c = customer.value
  const w = []
  if (c.overdueChains > 0)
    w.push(`ตั๋วค้างชำระ <strong>${c.overdueChains} ใบ</strong> (${fmtBaht(c.overduePrincipal)}) สูงสุด ${c.maxDaysOverdue} วัน`)
  if (c.earlyWarningAdj < 0)
    w.push('คะแนน Early Warning ติดลบ — ตั๋วเปิดเริ่มค้างชำระ')
  if (c.riskTrendAdj < 0)
    w.push('Risk Trend แย่ลงในช่วง 6 เดือนล่าสุด')
  return w
})

const trends = computed(() => {
  if (!customer.value) return []
  const c = customer.value
  return [
    { lbl: 'Risk Trend',               val: c.riskTrendAdj,         desc: trendDesc('risk',  c.riskTrendAdj) },
    { lbl: 'ต่อดอกช้า (Late Renewal)', val: c.lateRenewalTrendAdj,  desc: trendDesc('late',  c.lateRenewalTrendAdj) },
    { lbl: 'Early Warning',            val: c.earlyWarningAdj,      desc: trendDesc('warn',  c.earlyWarningAdj) },
    { lbl: 'Value Trend',              val: c.valueTrendAdj,        desc: trendDesc('value', c.valueTrendAdj) },
  ]
})

function fmtNum(n, dec = 0) {
  return Number(n).toLocaleString('th-TH', { minimumFractionDigits: dec, maximumFractionDigits: dec })
}

function fmtBaht(v) {
  v = Number(v)
  if (isNaN(v)) return '฿0'
  if (v >= 1000000) return '฿' + (v / 1000000).toFixed(1) + 'M'
  if (v >= 1000)    return '฿' + (v / 1000).toFixed(1) + 'K'
  return '฿' + Number(v).toLocaleString('th-TH', { maximumFractionDigits: 0 })
}

function fmtAdj(v) {
  if (v == null) return '—'
  const sign = v > 0 ? '+' : ''
  return `${sign}${Number(v).toFixed(1)}`
}

function adjClass(v) {
  if (v > 0) return 'adj-pos'
  if (v < 0) return 'adj-neg'
  return 'adj-neu'
}

function adjStyle(v) {
  if (v > 0) return 'color:var(--green)'
  if (v < 0) return 'color:var(--red)'
  return 'color:#9ca3af'
}

function bandCls(score)       { return score >= 67 ? 'band-high' : score >= 34 ? 'band-mid' : 'band-low' }
function bandLabelFull(score) { return score >= 67 ? 'สูง (HIGH)' : score >= 34 ? 'กลาง (MID)' : 'ต่ำ (LOW)' }
function dmiCls(dmi)          { return dmi >= 70 ? 'band-dmi-hi' : dmi >= 30 ? 'band-dmi-mid' : 'band-dmi-lo' }
function dmiLabel(dmi) {
  if (dmi >= 70) return 'ดี — ข้อมูลครบ'
  if (dmi >= 50) return 'พอใช้'
  if (dmi >= 30) return 'ปานกลาง'
  return 'ต่ำ — ข้อมูลน้อย'
}

function sigCls(v) { return v > 0 ? 'tc-pos' : v < 0 ? 'tc-neg' : 'tc-neu' }

function trendDesc(type, v) {
  if (v === 0) return 'ปกติ — ไม่เปลี่ยนแปลง'
  if (type === 'risk')  return v > 0 ? 'ดีขึ้น — อัตราหลุดลด'        : 'แย่ลง — อัตราหลุดสูงขึ้น'
  if (type === 'late')  return v > 0 ? 'ดีขึ้น — ต่อดอกตรงเวลา'      : 'แย่ลง — ต่อดอกช้ากว่าเดิม'
  if (type === 'warn')  return v === -5 ? 'ค้างชำระ 15–30% ของเงินต้น' : v === -10 ? 'ค้างชำระ 30–60%' : 'ค้างชำระ > 60%'
  if (type === 'value') return v > 0 ? 'ดีขึ้น — ดอกเบี้ยจ่ายเพิ่ม'  : 'แย่ลง — ดอกเบี้ยจ่ายลด'
  return ''
}
</script>

<style scoped>
.det-root {
  --bg: #f0f2f5;
  --surface: #ffffff;
  --border: #e2e6ea;
  --text: #1a1d23;
  --muted: #6b7280;
  --primary: #1d4ed8;
  --radius: 10px;
  --shadow: 0 1px 4px rgba(0,0,0,.08), 0 4px 16px rgba(0,0,0,.06);
  --green: #059669;
  --amber: #d97706;
  --red: #dc2626;
  font-family: var(--app-font);
  line-height: 1.5;
  background: var(--bg);
  color: var(--text);
  min-height: 100vh;
}

/* ── Page header ─────────────────────────────────────────────── */
.page-hdr {
  background: linear-gradient(135deg, #05602d 0%, #05af50 100%);
  color: #fff; padding: 15px 26px;
  display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
  box-shadow: 0 2px 10px rgba(0,0,0,.25);
  border-radius: var(--radius);
  text-shadow: 0 1px 4px rgba(0,0,0,.55);
}
.hdr-customer { flex: 1; min-width: 0; }
.hdr-name { font-size: 1.12rem; font-weight: 700; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; line-height: 1.5; }
.hdr-sub  { font-size: .75rem; color: rgba(255,255,255,.8); margin-top: 2px; }
.hdr-scores { display: flex; gap: 20px; align-items: center; margin-right: 6px; flex-shrink: 0; }
.hdr-score { text-align: center; }
.hdr-score-lbl { font-size: .64rem; color: #fff; text-transform: uppercase; letter-spacing: .5px; font-weight: 700; }
.hdr-score-val { font-size: 1.1rem; font-weight: 800; line-height: 1; padding: 4px 10px; border-radius: 8px; margin-top: 3px; }
.hdr-val-risk  { background: #fef9c3; color: #92400e; }
.hdr-val-value { background: #dbeafe; color: #1e40af; }
.hdr-val-dmi   { background: #ede9fe; color: #6d28d9; }
.hdr-div { width: 1px; height: 36px; background: rgba(255,255,255,.35); }
.back-btn {
  padding: 7px 15px; background: rgba(255,255,255,.14); color: #fff;
  border-radius: 7px; font-size: .8rem; font-weight: 600;
  border: 1px solid rgba(255,255,255,.2); cursor: pointer; white-space: nowrap; flex-shrink: 0;
}

/* ── Main body ───────────────────────────────────────────────── */
.det-main { width: 100%; padding: 16px 24px 32px; }
.section  { margin-bottom: 14px; }
.section-lbl {
  font-size: .68rem; font-weight: 700; color: var(--muted);
  text-transform: uppercase; letter-spacing: .6px; margin-bottom: 8px;
}
.cs-card { background: var(--surface); border-radius: var(--radius); box-shadow: var(--shadow); padding: 18px 20px; display: block; border: none; }
.cs-card-title { font-size: .68rem; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .6px; margin-bottom: 14px; }
.divider { height: 1px; background: var(--border); margin: 12px 0; }

/* ── Warning banner ──────────────────────────────────────────── */
.warn-banner {
  background: #fff7ed; border: 1px solid #fed7aa; border-left: 4px solid #f97316;
  border-radius: 8px; padding: 9px 16px; display: flex; align-items: flex-start; gap: 10px;
  font-size: .82rem; margin-bottom: 14px;
}
.warn-text { color: #7c2d12; }
.warn-text strong { font-weight: 700; }

/* ── Score hero grid ─────────────────────────────────────────── */
.score-hero-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 13px; }
.score-card {
  background: var(--surface); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 18px 20px;
  position: relative; overflow: hidden;
}
.score-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; }
.score-card.risk::before  { background: linear-gradient(90deg,#f59e0b,#fcd34d); }
.score-card.value::before { background: linear-gradient(90deg,#10b981,#6ee7b7); }
.score-card.dmi::before   { background: linear-gradient(90deg,#6366f1,#a5b4fc); }
.score-card-lbl   { font-size: .68rem; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 6px; }
.score-main-val   { font-size: 3rem; font-weight: 900; line-height: 1; margin-bottom: 6px; }
.risk  .score-main-val { color: #d97706; }
.value .score-main-val { color: #059669; }
.dmi   .score-main-val { color: #4f46e5; }
.score-band-badge { display: inline-flex; align-items: center; gap: 5px; padding: 2px 9px; border-radius: 20px; font-size: .74rem; font-weight: 700; margin-bottom: 12px; }
.band-high    { background: #d1fae5; color: #065f46; }
.band-mid     { background: #fef3c7; color: #92400e; }
.band-low     { background: #fee2e2; color: #991b1b; }
.band-dmi-hi  { background: #d1fae5; color: #065f46; }
.band-dmi-mid { background: #fef3c7; color: #92400e; }
.band-dmi-lo  { background: #fee2e2; color: #991b1b; }
.score-gauge-bg   { height: 9px; background: #f3f4f6; border-radius: 5px; overflow: hidden; margin-bottom: 5px; }
.score-gauge-fill { height: 100%; border-radius: 5px; }
.risk-fill  { background: linear-gradient(90deg,#fcd34d,#f59e0b); }
.value-fill { background: linear-gradient(90deg,#6ee7b7,#10b981); }
.dmi-fill   { background: linear-gradient(90deg,#a5b4fc,#6366f1); }
.score-range-lbl { display: flex; justify-content: space-between; font-size: .64rem; color: #9ca3af; margin-bottom: 12px; }
.score-meta { padding-top: 11px; border-top: 1px solid var(--border); display: flex; gap: 14px; flex-wrap: wrap; }
.score-meta-lbl { font-size: .63rem; color: var(--muted); text-transform: uppercase; letter-spacing: .4px; }
.score-meta-val { font-size: .9rem; font-weight: 700; margin-top: 1px; }

/* ── 2-col layout ────────────────────────────────────────────── */
.cs-col-2 { display: grid; grid-template-columns: 1.15fr .85fr; gap: 13px; width: 100%; }

/* ── Breakdown tables ────────────────────────────────────────── */
.breakdown-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.breakdown-hdr  { font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 10px; }
.risk-hdr   { color: #d97706; }
.value-hdr  { color: #059669; }
.breakdown-table { width: 100%; border-collapse: collapse; }
.breakdown-table tr { border-bottom: 1px solid #f1f5f9; }
.breakdown-table tr:last-child { border-bottom: none; }
.breakdown-table td { padding: 6px 0; font-size: .82rem; }
.breakdown-table td:first-child { color: var(--muted); }
.breakdown-table td:last-child  { text-align: right; font-weight: 700; }
.adj-lbl { font-size: .78rem !important; padding-left: 8px !important; }
.bk-divider td { border-top: 2px solid var(--border) !important; padding: 0 !important; }
.bk-final td   { font-size: 1rem !important; font-weight: 900 !important; padding-top: 8px !important; border-top: 2px solid #e5e7eb; }
.adj-pos { color: var(--green); }
.adj-neg { color: var(--red); }
.adj-neu { color: #9ca3af; }

/* ── Tier result box ─────────────────────────────────────────── */
.tier-result-box {
  margin-top: 16px; padding: 10px 12px;
  background: #f0f9ff; border-radius: 8px; border: 1px solid #bae6fd;
}
.tier-result-lbl  { font-size: .65rem; font-weight: 700; color: #0369a1; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 5px; }
.tier-result-body { font-size: .8rem; color: #075985; line-height: 1.8; }

/* ── Ticket stats ────────────────────────────────────────────── */
.ticket-stat-row { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }
.ticket-stat { background: #f8faff; border-radius: 8px; padding: 11px 13px; }
.ticket-stat-lbl { font-size: .67rem; color: var(--muted); text-transform: uppercase; letter-spacing: .4px; }
.ticket-stat-val { font-size: 1.55rem; font-weight: 800; line-height: 1.1; margin-top: 2px; }
.ticket-stat-sub { font-size: .7rem; color: var(--muted); margin-top: 2px; }
.tv-open    { color: var(--primary); }
.tv-overdue { color: var(--red); }
.tv-closed  { color: #475569; }
.tv-amber   { color: #d97706; }
.tv-ok      { color: #9ca3af; }

/* ── Overdue detail ──────────────────────────────────────────── */
.overdue-box { background: #fff5f5; border: 1px solid #fecaca; border-radius: 8px; padding: 12px 14px; margin-top: 2px; }
.overdue-box-lbl { font-size: .67rem; font-weight: 700; color: #b91c1c; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 8px; }
.overdue-row { display: flex; justify-content: space-between; font-size: .82rem; margin-bottom: 4px; }
.overdue-row:last-child { margin-bottom: 0; }
.overdue-key { color: #991b1b; }
.overdue-val { font-weight: 700; color: #b91c1c; }
.no-overdue  { font-size: .82rem; color: #059669; text-align: center; padding: 10px 0; }

/* ── Principal summary ───────────────────────────────────────── */
.principal-summary { margin-top: 10px; padding-top: 9px; border-top: 1px solid var(--border); }
.ps-row {
  display: flex; justify-content: space-between;
  font-size: .78rem; color: var(--muted); margin-bottom: 3px;
}
.ps-row strong { color: var(--text); }

/* ── Trend strip ─────────────────────────────────────────────── */
.trend-strip    { display: grid; grid-template-columns: repeat(4,1fr); gap: 12px; }
.trend-card     { background: var(--surface); border-radius: var(--radius); box-shadow: var(--shadow); padding: 14px 16px; text-align: center; }
.trend-card-lbl { font-size: .66rem; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 7px; }
.trend-card-val { font-size: 1.75rem; font-weight: 900; line-height: 1; }
.trend-card-desc{ font-size: .7rem; margin-top: 5px; font-weight: 600; }
.tc-pos { color: var(--green); }
.tc-neg { color: var(--red); }
.tc-neu { color: #9ca3af; }

/* ── History strip ───────────────────────────────────────────── */
.history-strip { display: grid; grid-template-columns: repeat(5,1fr); gap: 12px; }
.history-item  { background: var(--surface); border-radius: var(--radius); box-shadow: var(--shadow); padding: 14px 16px; }
.history-lbl   { font-size: .66rem; color: var(--muted); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 4px; }
.history-val   { font-size: 1.35rem; font-weight: 800; }
.history-sub   { font-size: .7rem; color: var(--muted); margin-top: 2px; }
.h-red   { color: var(--red); }
.h-amber { color: #d97706; }

/* ── Tier badges (header dark bg) ───────────────────────────── */
.tier-hdr { display: inline-flex; align-items: center; padding: 3px 11px; border-radius: 20px; font-size: .75rem; font-weight: 700; white-space: nowrap; text-shadow: none; }
.tier-hdr-Platinum { background: #ede9fe; color: #6d28d9; }
.tier-hdr-Gold     { background: #fef9c3; color: #92400e; }
.tier-hdr-Silver   { background: #f1f5f9; color: #475569; }
.tier-hdr-Watch    { background: #dbeafe; color: #1d4ed8; }
.tier-hdr-Preferred{ background: #e0f2fe; color: #075985; }
.tier-hdr-Standard { background: #eff6ff; color: #1e40af; }
.tier-hdr-Manage   { background: #fee2e2; color: #b91c1c; }
.tier-hdr-Guard    { background: #fef3c7; color: #92400e; }
.tier-hdr-Caution  { background: #fde8e8; color: #991b1b; }

/* ── Tier badges (light bg) ──────────────────────────────────── */
.tier-badge { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: .78rem; font-weight: 700; white-space: nowrap; }
.tier-Platinum { background: #ede9fe; color: #6d28d9; }
.tier-Gold     { background: #fef9c3; color: #92400e; }
.tier-Silver   { background: #f1f5f9; color: #475569; }
.tier-Watch    { background: #dbeafe; color: #1d4ed8; }
.tier-Preferred{ background: #e0f2fe; color: #075985; }
.tier-Standard { background: #eff6ff; color: #1e40af; }
.tier-Manage   { background: #fee2e2; color: #b91c1c; }
.tier-Guard    { background: #fef3c7; color: #92400e; }
.tier-Caution  { background: #fde8e8; color: #991b1b; }

/* ── Not-found state ─────────────────────────────────────────── */
.state-wrap    { text-align: center; padding: 80px 24px; }
.nd-icon       { font-size: 3.2rem; margin-bottom: 14px; }
.state-wrap h2 { font-size: 1.2rem; margin-bottom: 8px; }
.state-wrap p  { color: var(--muted); margin-bottom: 22px; }
.btn-back {
  display: inline-block; padding: 10px 26px; background: var(--primary);
  color: #fff; border: none; border-radius: 7px; font-weight: 600; font-size: .92rem; cursor: pointer;
}

/* ── Card title row with help button ─────────────────────────── */
.cs-card-title-row {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px;
}
.guide-btn {
  border-radius: 20px; padding: 3px 10px 3px 10px;
  border: 1.5px solid #c7d2fe; background: #eef2ff;
  color: #4f46e5; font-size: .72rem; font-weight: 800;
  cursor: pointer; flex-shrink: 0; line-height: 1;
  display: flex; align-items: center; gap: 5px;
  transition: background .15s, border-color .15s, transform .15s;
}
.guide-btn:hover {
  background: #4f46e5; border-color: #4f46e5; color: #fff;
  transform: scale(1.02);
}
.guide-btn-lbl { font-size: .7rem; font-weight: 600; }

/* ── Score guide modal ───────────────────────────────────────── */
.modal-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.modal-box {
  background: var(--surface); border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0,0,0,.25);
  width: 100%; max-width: 780px; max-height: 85vh;
  display: flex; flex-direction: column; overflow: hidden;
}
.modal-hdr {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 22px; border-bottom: 1px solid var(--border);
  font-size: .8rem; font-weight: 700; color: var(--muted);
  text-transform: uppercase; letter-spacing: .6px; flex-shrink: 0;
}
.modal-close {
  width: 28px; height: 28px; border-radius: 50%;
  border: none; background: #f3f4f6; color: var(--muted);
  font-size: .8rem; cursor: pointer; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: background .15s, color .15s;
}
.modal-close:hover { background: #fee2e2; color: var(--red); }
.modal-body { overflow-y: auto; padding: 0; }

/* ── Modal transition ────────────────────────────────────────── */
.modal-enter-active { transition: opacity .2s ease; }
.modal-leave-active { transition: opacity .18s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .modal-box,
.modal-leave-active .modal-box { transition: transform .2s ease, opacity .2s ease; }
.modal-enter-from .modal-box  { transform: scale(.95) translateY(12px); opacity: 0; }
.modal-leave-to   .modal-box  { transform: scale(.97) translateY(6px);  opacity: 0; }
.guide-table { width: 100%; border-collapse: collapse; font-size: .8rem; }
.guide-table thead tr { background: #f8fafc; border-bottom: 2px solid var(--border); }
.guide-table thead th {
  padding: 9px 14px; text-align: left; font-size: .67rem; font-weight: 700;
  color: var(--muted); text-transform: uppercase; letter-spacing: .5px;
}
.guide-table tbody tr { border-bottom: 1px solid #f1f5f9; }
.guide-table tbody tr:last-child { border-bottom: none; }
.guide-table tbody td { padding: 9px 14px; vertical-align: top; line-height: 1.55; color: #374151; }
.guide-table tbody td:first-child { width: 160px; }
.guide-table tbody td:last-child  { width: 220px; white-space: nowrap; }
.guide-name { font-weight: 700; color: var(--text) !important; font-size: .78rem; white-space: nowrap; }
.guide-sep td { padding: 0 !important; border-top: 2px solid var(--border) !important; }
.guide-final-row td { background: #f0f9ff; }
.guide-pos  { color: #059669; font-weight: 600; }
.guide-neg  { color: #dc2626; font-weight: 600; }
.guide-neu  { color: #9ca3af; font-weight: 600; }
.guide-hint { color: #6366f1; font-weight: 600; }
.band-pill  {
  display: inline-block; padding: 1px 8px; border-radius: 20px;
  font-size: .7rem; font-weight: 700; margin-right: 4px; margin-bottom: 2px;
}

@media (max-width: 1024px) {
  .score-hero-grid { grid-template-columns: 1fr; }
  .score-main-val  { font-size: 2.4rem; }
  .cs-col-2        { grid-template-columns: 1fr; }
  .breakdown-cols  { grid-template-columns: 1fr; }
  .history-strip   { grid-template-columns: repeat(3,1fr); }
  .trend-strip     { grid-template-columns: repeat(2,1fr); }
}
</style>
